/**
 * @timefly/core: the parts of Timefly that must not depend on a browser or on
 * React. Dates, the domain types the web app reads from the API, and the
 * working-time rules as pure functions. Shared by web/ and, later, mobile/.
 *
 * Rules here are the ones plan 0018 part two lists. They take plain objects
 * and return plain objects; nothing in this file touches the DOM or the API.
 */

/* ── dates ──────────────────────────────────────────────────────────────── */

export type ISODate = string; // 'YYYY-MM-DD'
export type HHMM = string; // 'HH:MM'

export const pad2 = (n: number): string => String(n).padStart(2, '0');
export const toUTC = (s: ISODate): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
};
export const iso = (d: Date): ISODate => d.toISOString().slice(0, 10);
export const addDays = (s: ISODate, n: number): ISODate => {
  const d = toUTC(s);
  d.setUTCDate(d.getUTCDate() + n);
  return iso(d);
};
/** 0 = Monday, 6 = Sunday. */
export const dow = (s: ISODate): number => (toUTC(s).getUTCDay() + 6) % 7;
export const mondayOf = (s: ISODate): ISODate => addDays(s, -dow(s));

/**
 * Norwegian public holidays, computed rather than stored, the same list and the same algorithm as
 * backend/services/norwegianDays.js: 1 January, skjærtorsdag, langfredag, 1. and 2. påskedag,
 * 1 and 17 May, Kristi himmelfartsdag, 1. and 2. pinsedag, 25 and 26 December (helligdagsloven §1
 * and the two høgtidsdager). The server cannot lend its copy to a browser, and a screen that has
 * to say whether a day was a helgedag cannot ask for one date at a time.
 */
const easterCache = new Map<number, ISODate>();
export function easterSunday(y: number): ISODate {
  const hit = easterCache.get(y);
  if (hit) return hit;
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = h + l - 7 * m + 114;
  const iso = new Date(Date.UTC(y, Math.floor(n / 31) - 1, (n % 31) + 1))
    .toISOString()
    .slice(0, 10) as ISODate;
  easterCache.set(y, iso);
  return iso;
}

const holidayCache = new Map<number, Set<string>>();
export function holidays(y: number): Set<string> {
  const hit = holidayCache.get(y);
  if (hit) return hit;
  const e = easterSunday(y);
  const off = (n: number) => addDays(e, n);
  const set = new Set([
    `${y}-01-01`,
    off(-3),
    off(-2),
    off(0),
    off(1),
    `${y}-05-01`,
    `${y}-05-17`,
    off(39),
    off(49),
    off(50),
    `${y}-12-25`,
    `${y}-12-26`,
  ]);
  holidayCache.set(y, set);
  return set;
}

/** A "søn- eller helgedag" in AML §10-10: a Sunday or a public holiday. */
export const isHolyDay = (s: ISODate): boolean => dow(s) === 6 || holidays(Number(s.slice(0, 4))).has(s);
export const weekDays = (mon: ISODate): ISODate[] => Array.from({ length: 7 }, (_, i) => addDays(mon, i));
export const isoWeek = (s: ISODate): number => {
  const d = toUTC(s);
  d.setUTCDate(d.getUTCDate() + 4 - (((d.getUTCDay() + 6) % 7) + 1));
  const y0 = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - y0.getTime()) / 864e5 + 1) / 7);
};
export const mins = (t: HHMM | null | undefined): number | null => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
export const hhmm = (m: number): HHMM =>
  `${pad2(Math.floor((((m % 1440) + 1440) % 1440) / 60))}:${pad2(((m % 60) + 60) % 60)}`;
export const inRange = (d: ISODate, a: ISODate, b: ISODate): boolean => d >= a && d <= b;

const lastSundayUTC = (y: number, month: number): number => {
  const e = Date.UTC(y, month + 1, 0);
  return e - new Date(e).getUTCDay() * 864e5;
};
/**
 * Epoch milliseconds of an Oslo wall-clock time, `m` minutes after midnight of `d` (past 1440 is
 * the next day). Summer time runs from the last Sunday of March to the last Sunday of October,
 * 01:00 UTC both. A repeated autumn hour reads as its first occurrence. ponytail: the EU rule is
 * written out, not read from Intl; if Norway stops changing clocks, this is the one place.
 */
export function osloMs(d: ISODate, m: number): number {
  const naive = toUTC(d).getTime() + m * 60000;
  const y = toUTC(d).getUTCFullYear();
  const on = lastSundayUTC(y, 2) + 36e5;
  const off = lastSundayUTC(y, 9) + 36e5;
  const summer = naive - 2 * 36e5;
  return summer >= on && summer < off ? summer : naive - 36e5;
}

/* ── domain types, the shapes the rules need ────────────────────────────── */

export type AveragingAgreement = 'none' | 'individual' | 'tillitsvalgte';

export interface Person {
  id: string;
  /**
   * Avtalt arbeidstid per uke. The norm, not the cap. Null when the caller is
   * not allowed to know it: the agreed hours reach administrators only, and the
   * two rules below compare against the contract, so they are skipped rather
   * than run against a guess. The statutory ceilings in `ceilings` are law and
   * apply to everybody, so they still fire (TF-SIMPLE-ROLES).
   */
  norm: number | null;
  /** Maks arbeidstid per uke. */
  cap?: number;
  breakMin?: number;
  paidBreak?: boolean;
  independent?: boolean; // §10-12 særlig uavhengig stilling
  avg?: AveragingAgreement; // §10-5, per person
  birthDate?: ISODate | undefined;
}

export interface ShiftLike {
  id: string;
  personId: string | null;
  date: ISODate;
  plannedFrom?: HHMM | null;
  plannedTo?: HHMM | null;
  punchIn?: HHMM | null;
  punchOut?: HHMM | null;
  breakIn?: HHMM | null;
  breakOut?: HHMM | null;
  /** Explicit pause in minutes, when known; otherwise derived from breakIn/breakOut. */
  pauseMin?: number | null;
  voided?: boolean;
  /** On-call shift type: hours count as a fraction (§10-4(3)), default 1/7. */
  onCallFraction?: number | null;
  /**
   * Minutes between the dated start and end instants when the source has dates
   * (an API shift, TF-REVIEW-FIRST-USE-20260915-02). Set, it decides the span; the
   * clock labels above are then only for display and the deviation checks. Unset
   * (a time-only template), the labels decide, with an end at or before the start
   * read as the next day.
   */
  spanMin?: number | null;
}

export interface TenantRules {
  /** Toleranse before a punch deviation is flagged, minutes. */
  toleranceMin: number;
  /** Break required above this many hours (§10-9: 5.5). */
  breakFromHours: number;
  today: ISODate;
}

const TOTAL_DAY_MAX = 13;

export type Severity = 'w' | 'd';
export interface RuleEntry {
  code:
    | 'open'
    | 'day_over_13'
    | 'day_over_max'
    | 'day_over'
    | 'break'
    | 'punch_in'
    | 'punch_out'
    | 'rest'
    | 'youth_night'
    | 'weekly'
    | 'week_over_max'
    | 'weekrest'
    | 'sundays';
  sev: Severity;
  /** Numbers the UI needs to phrase the message; the words live in the catalogue. */
  args: Record<string, number | string>;
}

/* ── durations ──────────────────────────────────────────────────────────── */

const start = (s: ShiftLike): HHMM | null | undefined => s.punchIn || s.plannedFrom;
const end = (s: ShiftLike): HHMM | null | undefined => s.punchOut || s.plannedTo;

/** Minutes between start and end, crossing midnight when needed. Null when either is unknown. */
export function span(s: ShiftLike): number | null {
  // dated instants win: the label projection loses the date and the offset, so a full
  // day read as 0 and a repeated autumn hour as 0 (TF-REVIEW-FIRST-USE-20260915-02)
  if (s.spanMin != null) return Math.max(0, s.spanMin);
  const f = mins(start(s));
  let t = mins(end(s));
  if (f == null || t == null) return null;
  if (t <= f) t += 1440;
  return t - f;
}

export function pauseMinutes(s: ShiftLike): number {
  if (s.pauseMin != null) return s.pauseMin;
  const a = mins(s.breakIn);
  let b = mins(s.breakOut);
  if (a == null || b == null) return 0;
  if (b < a) b += 1440;
  return b - a;
}

/** Worked hours: the span less the unpaid pause, scaled for on-call. Voided is zero. */
export function hoursOf(s: ShiftLike, p: Pick<Person, 'paidBreak'> | null): number {
  if (s.voided) return 0;
  const m = span(s);
  if (m == null) return 0;
  const h = Math.max(0, (m - (p?.paidBreak ? 0 : pauseMinutes(s))) / 60);
  return s.onCallFraction ? h / s.onCallFraction : h;
}

export const isPunched = (s: ShiftLike): boolean => !!(s.punchIn && s.punchOut);

/* ── worked time after breaks, the company's break policy (TF-TIME-TOTALS) ── */

/**
 * The company's break setting as the payroll report applies it (option R,
 * Codex 2026-09-14). `auto` true: the configured automatic length comes off a
 * shift longer than `breakFromHours`, recorded breaks are ignored for this
 * quantity; false: a complete recorded pair comes off, nothing else. This is
 * display policy for actual time; the rule engine keeps its own inputs.
 */
/**
 * How a company takes the break (plan 0032): the automatic one, the punched one, or the punched one
 * but never less than the automatic one. Each shift is paid under the mode stamped on it at punch-in.
 */
export type BreakMode = 'auto' | 'punched' | 'punched_min';
export interface BreakPolicy {
  auto: boolean;
  /** The company's mode; absent reads `auto` as auto and anything else as punched. */
  mode?: BreakMode | undefined;
  /** Effective automatic length in minutes, see effectiveAutoMinutes. */
  autoMinutes: number;
  /** The automatic deduction applies above this many hours (5.5 in the report). */
  breakFromHours: number;
}

/**
 * The report reads an empty value as 30 and 0 as no deduction (`lunch_length_minutes ?? 30`, since
 * 2026-09-22: a break staff cannot leave is working time, AML §10-9(1)); the same here, so totals
 * match payroll.
 */
export const effectiveAutoMinutes = (stored: number | null | undefined): number => stored ?? 30;

/** The actual instants of one shift, as the API stores them (ISO 8601 with offset or Z). */
export interface WorkInstants {
  punchIn: string | null | undefined;
  punchOut: string | null | undefined;
  breakIn?: string | null | undefined;
  breakOut?: string | null | undefined;
  voided?: boolean | undefined;
  /**
   * The stint this shift is part of when it runs back to back with another of the person's
   * shifts (16 to 00, then 00 to 06), as epoch ms: the automatic break is the stint's, taken once,
   * by the shift holding the 3.5 h mark (the pay report's autoBreakStints, 2026-10-06). Absent for a
   * shift on its own.
   */
  stint?: { start: number; end: number } | undefined;
  /**
   * The planned end, where ordinary working time ends: half an hour of a break recorded after it, when the
   * person worked more than two hours past it (the break not counted), is paid working time under aml
   * §10-9(2) and does not come off, as the backend's paidOvertimeBreakHours counts it (#252, WT-13).
   */
  plannedEnd?: string | null | undefined;
  /** The mode this shift was punched in under (shifts.break_mode); it wins over the company's. */
  breakMode?: BreakMode | null | undefined;
}
export type WorkedResult =
  | { status: 'ok'; minutes: number; deductedMinutes: number }
  /** a punch is missing: nothing to count yet */
  | { status: 'unavailable' }
  /** the instants cannot be a span (unparsable, or out before in) */
  | { status: 'invalid' };

const instant = (v: string | null | undefined): number | null => {
  if (!v) return null;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
};

/**
 * Full-shift worked minutes on the actual recorded instants (TF-TIME-INSTANTS),
 * elapsed time between punch in and punch out, so dates and offsets count and
 * a spring or fall transition is the real hour. Equal instants are 0 minutes;
 * out before in is invalid; a missing punch is unavailable. The deduction
 * mirrors the payroll report over the shift's own window, before rate rules:
 * `auto` takes the effective automatic length off a span longer than
 * `breakFromHours`, but never more than the time from the 3 h mark (the
 * report deducts from the hourly segment holding the 3,5 h mark onward);
 * recorded takes the overlap of the break with the span, so a break outside
 * the span, or a reversed one, deducts nothing.
 */
export function workedMinutesAt(w: WorkInstants, policy: BreakPolicy): WorkedResult {
  if (w.voided) return { status: 'ok', minutes: 0, deductedMinutes: 0 };
  if (!w.punchIn || !w.punchOut) return { status: 'unavailable' };
  const a = instant(w.punchIn);
  const b = instant(w.punchOut);
  if (a == null || b == null || b < a) return { status: 'invalid' };
  const span = (b - a) / 60000;
  const automatic = (): number => {
    const st = w.stint;
    if (st) {
      const mark = st.start + 3.5 * 3_600_000;
      return st.end - st.start > policy.breakFromHours * 3_600_000 && mark >= a && mark < b
        ? Math.min(policy.autoMinutes, (b - mark) / 60000)
        : 0;
    }
    return span > policy.breakFromHours * 60 ? Math.min(policy.autoMinutes, Math.max(0, span - 180)) : 0;
  };
  const recorded = (): number => {
    const p = instant(w.breakIn);
    const q = instant(w.breakOut);
    if (p == null || q == null || q <= p) return 0;
    const taken = Math.max(0, Math.min(b, q) - Math.max(a, p)) / 60000;
    // §10-9(2): the paid half hour after the ordinary day stays worked time
    const end = instant(w.plannedEnd);
    if (end == null || p < end) return taken;
    const afterEnd = (q - Math.max(p, end)) / 60000;
    const paid = (b - end) / 60000 - afterEnd > 120 ? Math.min(30, (q - p) / 60000) : 0;
    return Math.max(0, taken - paid);
  };
  const mode = w.breakMode ?? policy.mode ?? (policy.auto ? 'auto' : 'punched');
  // ponytail: punched_min is taken per shift here; the report takes it per stint, so a break punched in
  // one row of a back-to-back stint is not set against the other row. Pass the stint's punched minutes if that shows.
  const deducted = mode === 'auto' ? automatic() : mode === 'punched' ? recorded() : Math.max(recorded(), automatic());
  return { status: 'ok', minutes: span - deducted, deductedMinutes: deducted };
}
/** Hours from workedMinutesAt, null unless the result is ok. */
export const workedHoursAt = (w: WorkInstants, policy: BreakPolicy): number | null => {
  const r = workedMinutesAt(w, policy);
  return r.status === 'ok' ? r.minutes / 60 : null;
};

/* ── ceilings ───────────────────────────────────────────────────────────── */

export function ageOn(birth: ISODate | undefined, today: ISODate): number | null {
  if (!birth) return null;
  const b = toUTC(birth);
  const t = toUTC(today);
  let a = t.getUTCFullYear() - b.getUTCFullYear();
  const anniv = new Date(Date.UTC(t.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate()));
  if (t < anniv) a--;
  return a;
}

/** Daily and weekly ceilings for a person: §10-4 by default, §10-5 by agreement, §11 under 18. */
export function ceilings(p: Person, today: ISODate): { day: number; week: number; young: boolean } {
  const age = ageOn(p.birthDate, today);
  const young = age != null && age < 18;
  if (young) return { day: 8, week: 40, young };
  if (p.avg === 'tillitsvalgte') return { day: 12.5, week: 54, young };
  if (p.avg === 'individual') return { day: 10, week: 50, young };
  return { day: 9, week: 40, young };
}

/* ── rules on one shift ─────────────────────────────────────────────────── */

export function checkShift(s: ShiftLike, p: Person, prev: ShiftLike | null, rules: TenantRules): RuleEntry[] {
  const r: RuleEntry[] = [];
  if (!s.personId || s.voided || p.independent) return r;
  const h = hoursOf(s, p);
  const m = span(s);
  const c = ceilings(p, rules.today);
  const dayNorm = p.norm == null ? null : p.norm / 5;

  if (s.punchIn && !s.punchOut && s.date < rules.today) r.push({ code: 'open', sev: 'd', args: {} });

  if (c.young) {
    const f = mins(start(s));
    let t = mins(end(s));
    if (f != null && t != null) {
      if (t <= f) t += 1440;
      if (t > 21 * 60 || f < 6 * 60) r.push({ code: 'youth_night', sev: 'd', args: {} });
    }
  }

  // AML §10-6(10): total working time never over 13 h in 24 h. Past the ordinary ceiling (c.day)
  // the hours are overtime, which is lawful, so that is a warning since 2026-09-22 (law register
  // AML-10-6-10); a young worker's 8 h is itself the limit, so it stays red for them.
  if (h > TOTAL_DAY_MAX)
    r.push({ code: 'day_over_13', sev: 'd', args: { hours: round1(h), max: TOTAL_DAY_MAX } });
  else if (h > c.day)
    r.push({ code: 'day_over_max', sev: c.young ? 'd' : 'w', args: { hours: round1(h), max: c.day } });
  else if (dayNorm != null && isPunched(s) && h > dayNorm + 0.25)
    r.push({ code: 'day_over', sev: 'w', args: { hours: round1(h), max: round1(dayNorm) } });

  // §10-9(1): over breakFromHours some break; the person's full break (30 minutes) from 8 h (CV2, 2026-09-22)
  const need = p.breakMin ?? 30;
  const required = m != null && m >= 480 ? need : 1;
  if (isPunched(s) && m != null && m > rules.breakFromHours * 60 && need > 0 && pauseMinutes(s) < required)
    r.push({ code: 'break', sev: 'w', args: { pause: pauseMinutes(s), need: required } });

  if (s.punchIn && s.plannedFrom) {
    const late = (mins(s.punchIn) ?? 0) - (mins(s.plannedFrom) ?? 0);
    if (Math.abs(late) > rules.toleranceMin)
      r.push({
        code: 'punch_in',
        sev: 'w',
        args: { minutes: Math.abs(late), dir: late > 0 ? 'late' : 'early' },
      });
  }
  if (s.punchOut && s.plannedTo) {
    let diff = (mins(s.punchOut) ?? 0) - (mins(s.plannedTo) ?? 0);
    if (diff < -720) diff += 1440;
    if (Math.abs(diff) > rules.toleranceMin)
      r.push({
        code: 'punch_out',
        sev: 'w',
        args: { minutes: Math.abs(diff), dir: diff > 0 ? 'late' : 'early' },
      });
  }

  // §10-8(1): 11 h between the previous shift's end and this one's start, in real hours, so a
  // DST night counts the hour it gains or loses (CV6, W10)
  if (prev && prev.date === addDays(s.date, -1)) {
    const pe = mins(end(prev));
    const ps = mins(start(s));
    const pStart = mins(start(prev));
    if (pe != null && ps != null && pStart != null) {
      const endAbs = pe <= pStart ? pe + 1440 : pe;
      const rest = (osloMs(s.date, ps) - osloMs(prev.date, endAbs)) / 36e5;
      if (rest < 11) r.push({ code: 'rest', sev: 'w', args: { rest: round1(rest) } });
    }
  }
  return r;
}

/* ── rules on a week ────────────────────────────────────────────────────── */

export interface WeekResult {
  hours: number;
  /** The agreed week, or null when it is withheld from this reader. */
  norm: number | null;
  /** Hours against the agreed week, null when the agreement is not known. */
  diff: number | null;
  entries: RuleEntry[];
}

/**
 * @param shifts every shift of this person from the day before the week to the day after,
 *               so the 35-hour rest and the Sunday rule can look across the boundary.
 */
export function checkWeek(p: Person, monday: ISODate, shifts: ShiftLike[], rules: TenantRules): WeekResult {
  const days = weekDays(monday);
  const inWeek = shifts.filter((s) => days.includes(s.date) && !s.voided);
  const hours = inWeek.reduce((a, s) => a + hoursOf(s, p), 0);
  const entries: RuleEntry[] = [];
  const diff = p.norm == null ? null : hours - p.norm;
  if (p.independent) return { hours, norm: p.norm, diff, entries };
  const c = ceilings(p, rules.today);

  if (p.norm != null && hours > p.norm + 0.05)
    entries.push({
      code: 'weekly',
      sev: hours > c.week ? 'd' : 'w',
      args: { over: round1(hours - p.norm), norm: p.norm },
    });
  // §10-4 and §10-5: the ceiling comes from age and the averaging agreement, not from what
  // was agreed with this person, so a withheld agreement must not silence it. Only reached
  // when the entry above did not fire, which keeps a known agreement's wording as it was.
  else if (hours > c.week)
    entries.push({ code: 'week_over_max', sev: 'd', args: { hours: round1(hours), max: c.week } });

  // §10-8(2): one continuous 35 h rest in any 7 days; §11-5(3) 48 h under 18, judged whatever the
  // week's count of shifts, since three short shifts can break it (#246, WT-03, as the server)
  const restNeed = c.young ? 48 : 35;
  if (inWeek.length >= 4 || c.young) {
    const spans = shifts
      .filter((s) => !s.voided && s.date >= addDays(monday, -1) && s.date <= addDays(monday, 7))
      .map((s) => {
        const f = mins(start(s)) ?? 0;
        let t = mins(end(s)) ?? f + 480;
        if (t <= f) t += 1440;
        const zero = osloMs(monday, 0);
        return [(osloMs(s.date, f) - zero) / 36e5, (osloMs(s.date, t) - zero) / 36e5] as const;
      })
      .sort((a, b) => a[0] - b[0]);
    // the free time from the week's start to the first shift and from the last to the week's end counts
    // too («i løpet av sju dager»): Monday to Thursday with a free weekend has its rest (owner 18.10;
    // the server reads it the same way, shiftController longestWeekRest)
    let gap = Math.max(0, spans[0]?.[0] ?? 0);
    let until = spans[0]?.[1] ?? 0;
    for (let i = 1; i < spans.length; i++) {
      gap = Math.max(gap, (spans[i]?.[0] ?? 0) - until);
      until = Math.max(until, spans[i]?.[1] ?? 0);
    }
    gap = Math.max(gap, 168 - until);
    if (spans.length > 1 && gap > 0 && gap < restNeed)
      // red, as the server's own §10-8(2) entry is: one fact, one colour (2026-09-22)
      entries.push({ code: 'weekrest', sev: 'd', args: { gap: Math.round(gap) } });
  }

  // §10-8(4): every other Sunday free
  const sun = days[6] as ISODate;
  const prevSun = addDays(sun, -7);
  if (inWeek.some((s) => s.date === sun) && shifts.some((s) => s.date === prevSun && !s.voided))
    entries.push({ code: 'sundays', sev: 'w', args: {} });

  return { hours, norm: p.norm, diff, entries };
}

export const worst = (entries: RuleEntry[]): Severity | '' =>
  entries.some((e) => e.sev === 'd') ? 'd' : entries.length ? 'w' : '';

const round1 = (n: number): number => Math.round(n * 10) / 10;
